<template>
  <div class="bg-surface-raised flex flex-col gap-2 rounded-lg p-4">
    <div class="flex shrink-0 flex-row flex-wrap items-start gap-2">
      <div class="flex min-w-0 flex-1 flex-col">
        <div class="font-bold capitalize-first">
          {{
            $h.formatDateAndDiff(
              activity.created_at,
              'LLLL',
              $i18n.locale.substring(0, 2)
            )
          }}
        </div>
        <div class="text-xs leading-none font-semibold">
          # {{ activityTypeLabel }}
        </div>
        <RouterLink
          v-if="subjectLabel && subjectRoute"
          :to="subjectRoute"
          class="mt-0.5 text-sm text-blue-500 hover:underline dark:text-blue-400"
        >
          {{ subjectLabel }}
        </RouterLink>
        <div
          v-else-if="subjectLabel"
          class="mt-0.5 text-sm text-gray-500 dark:text-gray-400"
        >
          {{ subjectLabel }}
        </div>
      </div>
      <div class="flex shrink-0 flex-col">
        <button
          v-if="isPaymentSyncCauser"
          type="button"
          class="flew-row flex flex-1 items-center justify-end gap-2 hover:underline"
          :title="$t('invoicing.payment_sync_run_detail')"
          @click="openSyncRun"
        >
          <span class="font-semibold">
            {{ $t('invoicing.payment_sync_causer') }}
          </span>
          <img :src="chiftLogo" class="h-4" alt="" />
        </button>
        <div v-else class="flew-row flex flex-1 items-center justify-end gap-2">
          <span class="font-semibold">{{ user?.full_name }}</span>
          <UserAvatarPopover :user-id="activity.causer_id" size="extra-small" />
        </div>
        <div
          v-if="canEdit || canDelete"
          class="mt-1 flex flex-row justify-end gap-1"
        >
          <BaseButton
            v-if="canEdit"
            :title="$t('button.edit')"
            hide-title
            icon="pen"
            size="extra-small"
            secondary-color
            @click="$emit('edit', activity)"
          />
          <BaseButton
            v-if="canDelete"
            :title="$t('button.delete')"
            hide-title
            icon="trash"
            size="extra-small"
            color="danger"
            @click="$emit('delete', activity)"
          />
        </div>
      </div>
    </div>
    <div
      v-if="
        isModelActivity && !isCommissionActivity && propertiesAsArray.length
      "
      class="overflow-hidden rounded border"
    >
      <table class="w-full">
        <tr
          v-for="property in propertiesAsArray"
          :key="property.id"
          class="odd:bg-gray-100 dark:odd:bg-gray-800"
        >
          <td class="w-1/3 py-1.5 pr-2 pl-3 font-bold wrap-anywhere">
            {{ $t(`attributes.${property.attribute}`) }}
          </td>
          <td class="w-1/3 py-1.5 pr-2 wrap-anywhere">{{ property.old }}</td>
          <td class="w-1/3 py-1.5 pr-2 wrap-anywhere">{{ property.new }}</td>
        </tr>
      </table>
    </div>
    <ul
      v-else-if="isCommissionActivity"
      class="flex flex-col gap-1 text-sm empty:hidden"
    >
      <li v-if="commissionDetails.reason">
        <span class="font-semibold">{{ $t('global.reason') }} :</span>
        {{ commissionDetails.reason }}
      </li>
      <li v-if="commissionDetails.hasAmount" class="flex items-center gap-1">
        <span class="font-semibold">{{ $t('attributes.commission') }} :</span>
        <template v-if="commissionDetails.oldAmount !== null">
          <AmountFormatter
            class="text-gray-500 line-through"
            :amount="commissionDetails.oldAmount"
          />
          <FontAwesomeIcon :icon="['fal', 'arrow-right']" />
        </template>
        <AmountFormatter
          class="font-semibold"
          :amount="commissionDetails.newAmount"
        />
      </li>
      <li v-if="commissionDetails.baseAmount !== null">
        <span class="font-semibold">{{ $t('global.commission_base') }} :</span>
        <AmountFormatter
          class="inline"
          :amount="commissionDetails.baseAmount"
        />
      </li>
      <li
        v-for="field in commissionDetails.changedFields"
        :key="field.key"
        class="flex flex-wrap items-center gap-1"
      >
        <span class="font-semibold">{{ $t(field.label) }} :</span>
        <span class="text-gray-500 line-through">{{ field.old }}</span>
        <template v-if="field.new !== null">
          <FontAwesomeIcon :icon="['fal', 'arrow-right']" />
          <span class="font-semibold">{{ field.new }}</span>
        </template>
      </li>
      <li v-if="commissionDetails.moved">
        {{ commissionDetails.moved }}
      </li>
      <li
        v-if="commissionDetails.backfill"
        class="text-gray-500 dark:text-gray-400"
      >
        {{ $t('global.added_by_backfill') }}
      </li>
    </ul>
    <div v-else-if="isContactActivity" class="empty:hidden">
      <textarea
        v-if="activity.properties?.notes?.length"
        class="bg-surface-overlay min-h-20 w-full resize-none rounded-md p-2"
        :value="activity.properties.notes"
        readonly
      ></textarea>
    </div>
    <div
      v-else-if="mergeSummary"
      class="flex flex-col gap-1 text-sm text-gray-500 dark:text-gray-400"
    >
      <span>{{ mergeSummary }}</span>
      <ul v-if="reassignedReferences.length" class="flex flex-col gap-0.5 pl-4">
        <li
          v-for="reference in reassignedReferences"
          :key="reference.key"
          class="list-disc"
        >
          <span class="font-mono text-xs">{{ reference.key }}</span>
          <template v-if="reference.isDossier">
            <RouterLink
              v-for="id in reference.ids"
              :key="id"
              :to="{ name: 'dossier_form_edit_route', params: { id } }"
              class="ml-1 text-blue-500 underline hover:no-underline dark:text-blue-400"
              target="_blank"
            >
              {{ id }}
            </RouterLink>
          </template>
          <span v-else class="ml-1">{{ reference.ids.join(', ') }}</span>
          <span v-if="reference.hiddenCount > 0">
            (+{{ reference.hiddenCount }})
          </span>
        </li>
      </ul>
    </div>
    <div
      v-if="activity.tasks?.length"
      class="mt-1 grid grid-cols-2 gap-2 md:grid-cols-3"
    >
      <div
        v-for="task in activity.tasks"
        :key="task.id"
        class="group flex rounded-md shadow-sm dark:border dark:border-white dark:bg-slate-700"
      >
        <div
          class="flex flex-1 items-center justify-between gap-2 truncate rounded-md border border-gray-200 bg-white px-2 py-1 dark:bg-slate-700"
        >
          <div class="flex-1 truncate text-sm">
            <h4 class="truncate font-medium text-gray-900 dark:text-white">
              {{ task.name?.[$i18n.locale] }}
            </h4>
            <p
              v-if="task.due_date_time"
              class="text-xs text-gray-500 dark:text-gray-400"
            >
              {{
                $h.formatDate(
                  task.due_date_time,
                  task.due_date_undefined_time ? 'll' : 'lll'
                )
              }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <ChiftPaymentSyncRunModal
      v-if="openedRunId"
      :run-id="openedRunId"
      @close="openedRunId = null"
    />
  </div>
</template>

<script>
import UserAvatarPopover from '@c/Avatar/Popover/UserAvatarPopover.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import AmountFormatter from '@c/AmountFormatter.vue'
import ChiftPaymentSyncRunModal from '@c/Partner/Chift/ChiftPaymentSyncRunModal.vue'
import PartnersLogo from '@/assets/enums/config/PartnersLogo'
import { mapGetters } from 'vuex'
import { CONTACT_EVENTS } from '@u/constants'

/**
 * Not yet synced from Lokalise into this repository's lang files (lang/**
 * is never committed manually — see lokalise-translations skill), so $t()
 * still returns the raw key until that lands. Literal fallback for the
 * meantime, same pattern as the address book's own translate*Label helpers.
 */
const MERGE_JOURNAL_SUMMARY_KEY = 'global.merge_journal_summary'
const MERGE_JOURNAL_SUMMARY_FALLBACK =
  '{count} fiche(s) fusionnée(s), {references} référence(s) réattribuée(s)'

/**
 * Same not-yet-synced-from-Lokalise situation as MERGE_JOURNAL_SUMMARY_KEY,
 * for the `enums.activityLog.type.*` keys the address book's merge/archive
 * feature introduced (merge, archived, unarchived) — the title line had no
 * fallback at all until now, so it showed the raw key while the summary
 * below it (which does have a fallback) already read correctly.
 */
// the fields of a commission line a manager can change, with their label (the amount has its own line)
const COMMISSION_FIELD_LABELS = {
  name: 'attributes.name',
  description: 'attributes.description',
  internal_note: 'attributes.internal_note',
  quantity: 'attributes.quantity',
  unit_price_htva: 'global.unit_price_excluding_vat',
  vat_rate: 'global.tva',
  document_date: 'attributes.date'
}

const ACTIVITY_TYPE_FALLBACKS = {
  merge: 'fusion',
  archived: 'archivage',
  unarchived: 'désarchivage'
}

export default {
  name: 'ActivityBlock',
  components: {
    AmountFormatter,
    UserAvatarPopover,
    BaseButton,
    ChiftPaymentSyncRunModal
  },
  props: {
    activity: {
      type: Object,
      required: true
    },
    /**
     * Offers the edit and delete buttons on the activities a user logged
     * through the "log an activity" modal (CONTACT_EVENTS, the API's
     * Type::consignableTypes()). Opt-in: the block is reused by every
     * model's activity tab, where those actions have no place.
     */
    editable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['edit', 'delete'],
  data() {
    return {
      openedRunId: null
    }
  },
  computed: {
    ...mapGetters({
      users: 'metadata/getAllUserMetadata',
      initialMetadata: 'metadata/getInitialMetadata',
      dossier: 'dossier/getDossier'
    }),
    user() {
      return this.users(this.activity.causer_id)
    },
    /**
     * The activity causer is a model, not necessarily a User. A scheduled sync has no
     * authenticated user, so it causes its writes as the run itself — which answers
     * "by whom" and "by which run" in one go.
     */
    isPaymentSyncCauser() {
      return this.activity.causer_type === 'App\\Models\\PaymentSyncRun'
    },
    chiftLogo() {
      return PartnersLogo.chift
    },
    activityTypeLabel() {
      const key = `enums.activityLog.type.${this.activity.description}`
      const translated = this.$t(key)

      if (translated !== key) {
        return translated
      }

      return ACTIVITY_TYPE_FALLBACKS[this.activity.description] ?? translated
    },
    subjectLabel() {
      // An item dossier (e.g. an Enaos ribbon) has no loaded subject: the API sends its name.
      if (this.activity.subject_type === 'App\\Models\\ItemDossier') {
        return this.activity.subject_label
          ? `${this.$t('attributes.item')} : ${this.activity.subject_label}`
          : null
      }
      const subject = this.activity.subject
      if (!subject) return null
      if (this.activity.subject_type === 'App\\Models\\Company') {
        return `${this.$t('attributes.company')} : ${subject.name}`
      } else if (this.activity.subject_type === 'App\\Models\\FuneralHome') {
        return `${this.$t('attributes.funeral_home')} : ${subject.name}`
      } else if (this.activity.subject_type === 'App\\Models\\Site') {
        return `${this.$t('attributes.site')} : ${subject.name}`
      } else if (this.activity.subject_type === 'App\\Models\\Dossier') {
        return [
          this.$t('attributes.dossier'),
          ':',
          subject.reference,
          subject.concernedPerson?.full_name
        ]
          .filter(Boolean)
          .join(' ')
      } else if (this.activity.subject_type === 'App\\Models\\Person') {
        return `${this.$t('attributes.person')} : ${subject.full_name}`
      } else if (this.activity.subject_type === 'App\\Models\\Contact') {
        return `${this.$t('attributes.contact')} : ${subject.full_name}`
      } else if (this.activity.subject_type === 'App\\Models\\Event') {
        return `${this.$t('attributes.event')} : ${subject.title}`
      } else if (this.activity.subject_type === 'App\\Models\\TaskDossier') {
        return `${this.$t('attributes.task')} : ${subject.name[this.$i18n.locale]}`
      }
      const subjectType = this.activity.subject_type?.split('\\').pop()
      const name =
        typeof subject.name === 'object'
          ? (subject.name[this.$i18n.locale] ??
            subject.name[Object.keys(subject.name)[0]] ??
            null)
          : (subject.name ?? null)

      if (subjectType === 'WikiPlace' && subject.type) {
        const typeLabel = this.initialMetadata?.enums?.place_types?.find(
          (p) => p.id === subject.type
        )?.name
        return [typeLabel, name].filter(Boolean).join(' — ')
      }

      return name
    },
    subjectRoute() {
      if (
        this.activity.subject_type === 'App\\Models\\Dossier' &&
        this.activity.subject_id
      ) {
        return {
          name: 'dossier_form_edit_route',
          params: { id: this.activity.subject_id }
        }
      }
      return null
    },
    isContactActivity() {
      return CONTACT_EVENTS.includes(this.activity.description)
    },
    canEdit() {
      return (
        this.editable &&
        this.isContactActivity &&
        this.$can('manage_activities')
      )
    },
    canDelete() {
      return (
        this.editable &&
        this.isContactActivity &&
        this.$can('delete_activities')
      )
    },
    isCommissionActivity() {
      return !!this.activity.description?.startsWith('commission_')
    },
    commissionDetails() {
      const attributes = this.activity.properties?.attributes ?? {}
      const old = this.activity.properties?.old ?? {}
      const reason =
        attributes.reason ??
        (this.activity.description === 'commission_item_recalculated'
          ? 'recalculated'
          : null)
      const hasValue = (value) => value !== undefined && value !== null

      return {
        reason: reason ? this.commissionReasonLabel(reason) : null,
        hasAmount: hasValue(attributes.commission_htva),
        oldAmount: hasValue(old.commission_htva) ? old.commission_htva : null,
        newAmount: attributes.commission_htva,
        // a base of zero (a free line has none) says nothing: it is not shown
        baseAmount:
          hasValue(attributes.base_amount) &&
          Number(attributes.base_amount) !== 0
            ? attributes.base_amount
            : null,
        // what a manager changed on a line (the amount has its own line above): what it was, and what it became when the API says
        changedFields: Object.keys(COMMISSION_FIELD_LABELS)
          .filter((key) => key in old)
          .map((key) => ({
            key,
            label: COMMISSION_FIELD_LABELS[key],
            old: hasValue(old[key])
              ? this.formatChangedValue(key, old[key])
              : '-',
            new: hasValue(attributes[key])
              ? this.formatChangedValue(key, attributes[key])
              : null
          })),
        backfill: attributes.source === 'backfill',
        moved:
          this.activity.description === 'commission_item_moved'
            ? this.$t('global.commission_item_moved_activity', {
                from: this.users(attributes.from_user_id)?.full_name ?? '',
                to: this.users(attributes.to_user_id)?.full_name ?? ''
              })
            : null
      }
    },
    isModelActivity() {
      return !!this.activity.properties.attributes
    },
    /**
     * A merge activity's `properties` (MergeJournal::record()) carry
     * `absorbed_ids` and `reassigned_counts` rather than the before/after
     * `attributes` an ordinary model edit does — `isModelActivity` and
     * `isContactActivity` both stay false for it, and without this the
     * activity block showed only the principal's name, nothing about the
     * merge itself. `reassigned_counts` is `{table.column: count}`; the sum
     * across every entry is how many references moved in total, regardless
     * of which table they lived on.
     */
    mergeSummary() {
      if (this.activity.description !== 'merge') {
        return null
      }

      const absorbedCount = this.activity.properties?.absorbed_ids?.length ?? 0
      const referenceCount = Object.values(
        this.activity.properties?.reassigned_counts ?? {}
      ).reduce((sum, count) => sum + count, 0)

      const translated = this.$t(MERGE_JOURNAL_SUMMARY_KEY, {
        count: absorbedCount,
        references: referenceCount
      })

      return (
        translated === MERGE_JOURNAL_SUMMARY_KEY
          ? MERGE_JOURNAL_SUMMARY_FALLBACK
          : translated
      )
        .replace('{count}', absorbedCount)
        .replace('{references}', referenceCount)
    },
    /**
     * `reassigned_record_ids` (MergeService::JOURNAL_SAMPLE_LIMIT) samples up
     * to 20 moved row ids per "table.column" — reassigned_counts carries the
     * true total, so hiddenCount fills the gap when a table moved more than
     * the sample kept. Only "dossiers" has a known edit route here; every
     * other table (payments, invoices, event_properties...) has no page to
     * link to, so its ids stay plain text.
     */
    reassignedReferences() {
      if (this.activity.description !== 'merge') {
        return []
      }

      const counts = this.activity.properties?.reassigned_counts ?? {}
      const recordIds = this.activity.properties?.reassigned_record_ids ?? {}

      return Object.keys(counts)
        .filter((key) => counts[key] > 0)
        .map((key) => {
          const ids = recordIds[key] ?? []
          return {
            key,
            ids,
            hiddenCount: Math.max(counts[key] - ids.length, 0),
            isDossier: key.startsWith('dossiers.')
          }
        })
    },
    propertiesAsArray: function () {
      if (!this.activity.properties.attributes) {
        return []
      }

      const normalize = (value) => {
        if (value === null || value === undefined) return null
        if (value === '' || value === '---') return null
        if (value === 0 || value === 0.0 || value === '0' || value === '0.00')
          return null
        return value
      }

      return Object.keys(this.activity.properties.attributes)
        .map((key) => ({
          attribute: key,
          old:
            this.formatValue(normalize(this.activity.properties.old?.[key])) ??
            '---',
          new:
            this.formatValue(
              normalize(this.activity.properties.attributes[key])
            ) ?? '---'
        }))
        .filter((line) => {
          const oldVal = line.old
          const newVal = line.new

          const isDifferent = JSON.stringify(oldVal) !== JSON.stringify(newVal)

          return isDifferent
        })
    }
  },
  methods: {
    // a reason the translations do not know yet reads as its words, not as the raw key
    commissionReasonLabel(reason) {
      const key = `enums.activityLog.commission_reason.${reason}`
      const label = this.$t(key)

      return label === key ? reason.replaceAll('_', ' ') : label
    },
    // the date, the price and the rate of a commission line read as they do on the line itself
    formatChangedValue(key, value) {
      if (key === 'document_date') return this.$h.formatDate(value, 'L')
      if (key === 'unit_price_htva') return this.$h.formatCurrency(value)
      if (key === 'vat_rate') return `${value} %`

      return value
    },
    openSyncRun() {
      this.openedRunId = this.activity.causer_id
    },
    formatValue(value) {
      if (value === null || value === undefined) return null
      if (Array.isArray(value)) {
        const labels = value
          .map((item) => this.formatItem(item))
          .filter(Boolean)
        return labels.length ? labels.join(', ') : null
      }
      if (typeof value === 'object') {
        return this.formatItem(value)
      }
      return value
    },
    formatItem(item) {
      if (typeof item !== 'object' || item === null) {
        const dossierItem = this.dossier?.dossier_items?.find(
          (di) =>
            String(di.id) === String(item) ||
            String(di.item_id) === String(item)
        )
        if (dossierItem?.item?.name) {
          const name = dossierItem.item.name
          return typeof name === 'object'
            ? (name[this.$i18n.locale] ??
                name[Object.keys(name)[0]] ??
                String(item))
            : String(name)
        }
        return String(item)
      }
      // Ressource humaine (user)
      if (item.full_name) return item.full_name
      // Ressource matérielle avec nom traduit
      if (item.name && typeof item.name === 'object') {
        return (
          item.name[this.$i18n.locale] ??
          item.name[Object.keys(item.name)[0]] ??
          item.initials ??
          null
        )
      }
      if (typeof item.name === 'string') return item.name
      return item.initials ?? null
    }
  }
}
</script>
